import { useState } from 'react';

import { Icon } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import useCurrency, { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';

interface FeeConfigAccordionProps {
  className?: string;
  feeConfig: {
    category: string;
    amount: string;
  }[];
  totalAmount?: string;
  fineAmount?: string;
  subTotalAmount?: string;
  closedAmount?: string;
  gstInfo?: {
    cgstPercent?: string;
    sgstPercent?: string;
    igstPercent?: string;
    cgstAmount?: string;
    sgstAmount?: string;
    igstAmount?: string;
    totalGst?: string;
  };
  discountAmount?: string;
}

const FeeConfigAccordion = ({
  className,
  feeConfig,
  totalAmount,
  fineAmount,
  subTotalAmount,
  closedAmount,
  gstInfo,
  discountAmount
}: FeeConfigAccordionProps) => {
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);
  const { currencyConfig } = useCurrency();

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  // const cgstpercent = 20; // convert to a prop having value from api
  // const sgstpercent = 20; // convert to a prop having value from api
  // const cgstAmount = 20; // convert to a prop having value from api
  // const sgstAmount = 20; // convert to a prop having value from api
  // const totalGstAmount = 20; // convert to a prop having value from api
  // const iscgst = true; // convert to a prop having value from api
  // const issgst = true; // convert to a prop having value from api
  return (
    <div className={`rounded-xl border border-grey40 p-4 ${className || ''}`}>
      {openAccordion ? (
        <>
          {feeConfig.map(
            (item, index) =>
              Number(item.amount) !== 0 && (
                <div
                  className={`${
                    index !== 0 ? 'mt-3' : ''
                  } flex justify-between`}
                  key={item.category}
                >
                  <div className="text-sm font-normal text-grey">
                    {item.category}
                  </div>
                  <div className="flex items-center justify-center text-sm font-normal text-content">
                    <CurrencyAmount
                      value={Number(item.amount)}
                      className={`${
                        isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                      }`}
                      symbolClass={`${
                        isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                      }`}
                      valueClass=""
                    />
                  </div>
                </div>
              )
          )}
          <div
            className={`mt-3 flex cursor-pointer justify-between  ${
              feeConfig.length !== 0
                ? 'border-t border-t-grey40 pt-3'
                : 'border-b border-b-grey40 pb-3'
            }`}
          >
            <div className="text-sm font-normal text-grey">Sub Total</div>
            <div className="flex items-center justify-center text-sm font-normal text-content">
              <CurrencyAmount
                value={Number(subTotalAmount)}
                className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                symbolClass={`${
                  isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                }`}
                valueClass=""
              />
            </div>
          </div>
          {fineAmount && parseFloat(fineAmount) > 0 && (
            <div className="flex cursor-pointer justify-between pt-3">
              <div className="text-sm font-normal text-grey">Fine</div>
              <div className="flex items-center justify-center text-sm font-normal text-errorLite5">
                <CurrencyAmount
                  value={Number(fineAmount)}
                  className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                  symbolClass={`${
                    isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                  }`}
                  valueClass=""
                />
              </div>
            </div>
          )}

          {(Number(gstInfo?.cgstAmount) > 0 ||
            Number(gstInfo?.sgstAmount) > 0 ||
            Number(gstInfo?.igstAmount) > 0) && (
            <>
              <div className="flex cursor-pointer flex-col gap-3 pt-3">
                {Number(gstInfo?.cgstAmount) > 0 && (
                  <div className="flex justify-between">
                    <div className="text-sm font-normal text-grey">{`CGST (${gstInfo?.cgstPercent}%)`}</div>
                    <div className="flex items-center justify-center text-sm font-normal ">
                      <CurrencyAmount
                        value={Number(gstInfo?.cgstAmount)}
                        className={`${
                          isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                        }`}
                        symbolClass={`${
                          isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                        }`}
                        valueClass=""
                      />
                    </div>
                  </div>
                )}
                {Number(gstInfo?.sgstAmount) > 0 && (
                  <div className="flex justify-between">
                    <div className="text-sm font-normal text-grey">{`SGST (${gstInfo?.sgstPercent}%)`}</div>
                    <div className="flex items-center justify-center text-sm font-normal ">
                      <CurrencyAmount
                        value={Number(gstInfo?.sgstAmount)}
                        className={`${
                          isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                        }`}
                        symbolClass={`${
                          isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                        }`}
                        valueClass=""
                      />
                    </div>
                  </div>
                )}
                {Number(gstInfo?.igstAmount) > 0 && (
                  <div className="flex justify-between">
                    <div className="text-sm font-normal text-grey">{`IGST (${gstInfo?.igstPercent}%)`}</div>
                    <div className="flex items-center justify-center text-sm font-normal ">
                      <CurrencyAmount
                        value={Number(gstInfo?.igstAmount)}
                        className={`${
                          isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                        }`}
                        symbolClass={`${
                          isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                        }`}
                        valueClass=""
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex cursor-pointer justify-between border-t border-t-grey40 pt-3 text-sm font-normal ">
                <div className="text-grey">Total GST</div>
                <div className="flex items-center justify-center text-sm font-normal ">
                  <CurrencyAmount
                    value={Number(gstInfo?.totalGst)}
                    className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                    symbolClass={`${
                      isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                    }`}
                    valueClass=""
                  />
                </div>
              </div>
            </>
          )}
          {discountAmount && parseFloat(discountAmount) > 0 && (
            <div className="mt-3 flex cursor-pointer justify-between border-t border-t-grey40 pt-3">
              <div className="text-sm font-normal text-grey">
                Discount Amount
              </div>
              <div className="flex items-center justify-center text-sm font-normal text-content">
                -
                <CurrencyAmount
                  value={Number(discountAmount)}
                  className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                  symbolClass={`${
                    isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                  }`}
                  valueClass=""
                />
              </div>
            </div>
          )}
          {closedAmount && parseFloat(closedAmount) > 0 && (
            <div className={`flex cursor-pointer justify-between pt-3 `}>
              <div className="text-sm font-normal text-grey">Closed Amount</div>
              <div className="flex items-center justify-center text-sm font-normal ">
                -
                <CurrencyAmount
                  value={Number(closedAmount)}
                  className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                  symbolClass={`${
                    isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                  }`}
                  valueClass=""
                />
              </div>
            </div>
          )}
          <div
            className="mt-3 flex cursor-pointer justify-between border-t border-t-grey40"
            role="presentation"
            onClick={() => setOpenAccordion(!openAccordion)}
          >
            <div className="mt-4 flex items-center gap-2">
              <div className="text-sm font-medium text-primary">
                Total Amount
              </div>
              <Icon
                name="down-icon-green"
                size="extrasmall"
                className={openAccordion ? 'rotate-180' : ''}
              />
            </div>
            <div className="mt-4 flex items-center justify-center">
              <CurrencyAmount
                value={Number(totalAmount)}
                className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                symbolClass={`${
                  isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                }`}
                valueClass=""
              />
            </div>
          </div>
        </>
      ) : (
        <div
          className="flex cursor-pointer justify-between"
          role="presentation"
          onClick={() => setOpenAccordion(!openAccordion)}
        >
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-primary">Total Amount</div>
            <Icon
              name="down-icon-green"
              size="extrasmall"
              className={openAccordion ? 'rotate-180' : ''}
            />
          </div>
          <div className="flex items-center justify-center">
            <CurrencyAmount
              value={Number(totalAmount)}
              className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
              symbolClass={`${
                isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
              }`}
              valueClass=""
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeConfigAccordion;
